import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Center, Spinner, Box, Text, Heading, Alert, AlertIcon } from '@chakra-ui/react';
import { supabase } from '../lib/supabaseClient';

const EmailLink = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationType, setVerificationType] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        // Parse URL parameters
        const searchParams = new URLSearchParams(location.search);
        const tokenHash = searchParams.get('token_hash');
        const type = searchParams.get('type');

        // Validate parameters
        if (!tokenHash) {
          throw new Error('Missing token hash in URL parameters');
        }

        if (!type) {
          throw new Error('Missing verification type in URL parameters');
        }

        // Set verification type for display purposes
        setVerificationType(type);

        // Verify the token with Supabase
        console.log(`Verifying token with type: ${type}`);
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as any
        });

        if (error) {
          throw error;
        }

        console.log('Token verification successful');
        
        // Short delay before redirect
        setTimeout(() => {
          // Redirect to main page
          navigate('/');
        }, 1500);
      } catch (err: any) {
        console.error('Token verification error:', err);
        setError(err.message || 'An error occurred during verification');
      } finally {
        setIsProcessing(false);
      }
    };

    verifyToken();
  }, [navigate, location.search]);

  const getVerificationTypeDisplay = () => {
    switch (verificationType) {
      case 'signup':
        return 'account signup';
      case 'invite':
        return 'invitation';
      case 'magiclink':
        return 'magic link authentication';
      case 'recovery':
        return 'account recovery';
      case 'email_change':
        return 'email change';
      case 'email':
        return 'email verification';
      default:
        return 'verification';
    }
  };

  return (
    <Center height="100vh" bg="gray.900">
      <Box
        p={8}
        maxWidth="400px"
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        textAlign="center"
        bg="gray.800"
        color="white"
      >
        {isProcessing ? (
          <>
            <Spinner size="xl" color="green.400" mb={4} />
            <Heading size="md" mb={2}>
              Verifying your {verificationType ? getVerificationTypeDisplay() : 'account'}
            </Heading>
            <Text color="gray.400">Please wait while we process your request...</Text>
          </>
        ) : error ? (
          <>
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
            <Text>Redirecting to home page in a few seconds...</Text>
          </>
        ) : (
          <>
            <Heading size="md" mb={4} color="green.400">
              Verification Successful!
            </Heading>
            <Text mb={2}>Your {getVerificationTypeDisplay()} has been verified.</Text>
            <Text color="gray.400">Redirecting to the main page...</Text>
          </>
        )}
      </Box>
    </Center>
  );
};

export default EmailLink; 